/**
 * A report PDF that rendered but could not be uploaded, held in memory so the
 * user can save it with a tap instead of losing the work.
 *
 * This used to trigger a download straight away. iOS Safari doesn't download
 * a blob link: it navigates the tab to it, which replaced the app (and its
 * error message) with the PDF, inside the same page process that had just
 * used ~1.6 GB to render it. Scrolling the PDF then pushed that process past
 * iOS's 2 GB limit, and the PDF, which only existed in that page, was lost.
 * Saving from a tap instead hands the file to the share sheet on touch
 * devices (no navigation) and to a normal download elsewhere.
 *
 * In-memory only, so it is gone after a reload; one PDF at a time.
 */

interface UnsavedPdf {
  reportId: string
  blob: Blob
  filename: string
}

let unsaved: UnsavedPdf | null = null

export function keepUnsavedPdf(reportId: string, blob: Blob, filename: string): void {
  unsaved = { reportId, blob, filename }
}

export function hasUnsavedPdf(reportId: string): boolean {
  return unsaved?.reportId === reportId
}

export function discardUnsavedPdf(reportId: string): void {
  if (unsaved?.reportId === reportId) unsaved = null
}

/**
 * Saves the report's unsaved PDF. Must be called from a tap: the share sheet
 * and the download both need a user gesture. Returns false when there is
 * nothing to save or the user dismissed the share sheet.
 */
export async function saveUnsavedPdf(reportId: string): Promise<boolean> {
  if (unsaved?.reportId !== reportId) return false
  const { blob, filename } = unsaved

  const file = new File([blob], filename, { type: 'application/pdf' })
  const touch = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
  if (touch && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] })
      return true
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return false
      // Share failed for another reason; fall back to a download
    }
  }

  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return true
}
